/**
 * Reusable Supabase Authentication Service
 *
 * This module can be used standalone (no build step) or imported into
 * build-based projects (Vite, Webpack, etc.).
 *
 * Usage:
 *
 * ```javascript
 * import AuthService from './auth-service.js';
 *
 * const auth = new AuthService(supabaseUrl, supabaseKey);
 * await auth.initialize();
 *
 * auth.onAuthChange((user) => {
 *     if (user) {
 *         console.log('Signed in:', user.email);
 *     }
 * });
 * ```
 */

class AuthService {
    /**
     * @param {string} supabaseUrl - Supabase project URL
     * @param {string} supabaseKey - Supabase anon key
     */
    constructor(supabaseUrl, supabaseKey) {
        this.supabaseUrl = supabaseUrl;
        this.supabaseKey = supabaseKey;
        this.supabase = null;
        this.user = null;
        this.session = null;
        this.authCallbacks = [];
        this._initialized = false;
    }

    /**
     * Initialize the auth service and check for existing session
     * Must be called before using other methods
     */
    async initialize() {
        try {
            console.log('[AuthService] Initializing...');

            // Create Supabase client
            if (typeof window !== 'undefined' && window.supabase) {
                // Browser environment with Supabase CDN
                this.supabase = window.supabase.createClient(this.supabaseUrl, this.supabaseKey);
            } else if (typeof window !== 'undefined' && window.createClient) {
                // Browser environment with module import
                this.supabase = window.createClient(this.supabaseUrl, this.supabaseKey);
            } else {
                throw new Error('Supabase client not available. Include @supabase/supabase-js or use CDN.');
            }

            // Check for existing session
            const { data: { session }, error } = await this.supabase.auth.getSession();

            if (error) {
                console.error('[AuthService] Error getting session:', error);
                this.notifyAuthChange(null);
                this._initialized = true;
                return;
            }

            if (session) {
                console.log('[AuthService] Found existing session for:', session.user.email);
                this.session = session;
                this.user = session.user;
                this.notifyAuthChange(this.user);
            } else {
                console.log('[AuthService] No existing session');
                this.notifyAuthChange(null);
            }

            // Listen for auth state changes
            this.supabase.auth.onAuthStateChange((event, session) => {
                console.log('[AuthService] Auth state changed:', event, session?.user?.email);
                this.session = session;
                this.user = session?.user || null;
                this.notifyAuthChange(this.user);
            });

            this._initialized = true;
            console.log('[AuthService] Initialized successfully');
        } catch (error) {
            console.error('[AuthService] Initialization error:', error);
            this.notifyAuthChange(null);
            this._initialized = true;
            throw error;
        }
    }

    /**
     * Register a callback to be notified of auth state changes
     * @param {Function} callback - Called with user object or null
     */
    onAuthChange(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        this.authCallbacks.push(callback);

        // Immediately call with current state if initialized
        if (this._initialized) {
            callback(this.user);
        }
    }

    /**
     * Notify all registered callbacks of auth state change
     * @private
     */
    notifyAuthChange(user) {
        this.authCallbacks.forEach(callback => {
            try {
                callback(user);
            } catch (error) {
                console.error('[AuthService] Error in auth callback:', error);
            }
        });
    }

    /**
     * Sign in with Google OAuth
     * Redirects to Google for authentication
     */
    async signIn() {
        try {
            if (!this._initialized) {
                throw new Error('AuthService not initialized. Call initialize() first.');
            }

            const { error } = await this.supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin
                }
            });

            if (error) throw error;
        } catch (error) {
            console.error('[AuthService] Sign in error:', error);
            throw error;
        }
    }

    /**
     * Sign out current user
     */
    async signOut() {
        try {
            if (!this._initialized) {
                throw new Error('AuthService not initialized. Call initialize() first.');
            }

            const { error } = await this.supabase.auth.signOut();
            if (error) throw error;

            console.log('[AuthService] User signed out');
        } catch (error) {
            console.error('[AuthService] Sign out error:', error);
            throw error;
        }
    }

    /**
     * Get current user's access token (JWT)
     * Used for authenticating API requests
     * @returns {string|null} JWT token or null if not signed in
     */
    getAccessToken() {
        return this.session?.access_token || null;
    }

    /**
     * Check if user is signed in
     * @returns {boolean}
     */
    isAuthenticated() {
        return this.user !== null;
    }

    /**
     * Get current user object
     * @returns {object|null} User object or null
     */
    getUser() {
        return this.user;
    }

    /**
     * Get user's organizations
     * @param {string} userId - User ID (defaults to current user)
     * @returns {Promise<Array>} List of organizations
     */
    async getUserOrganizations(userId = null) {
        try {
            if (!this._initialized) {
                throw new Error('AuthService not initialized');
            }

            const targetUserId = userId || this.user?.id;
            if (!targetUserId) {
                throw new Error('No user ID provided and no user signed in');
            }

            const { data, error } = await this.supabase
                .from('user_org_memberships')
                .select(`
                    org_id,
                    role,
                    organizations!inner(id, name)
                `)
                .eq('user_id', targetUserId);

            if (error) throw error;

            return data.map(item => ({
                id: item.organizations.id,
                name: item.organizations.name,
                role: item.role
            }));
        } catch (error) {
            console.error('[AuthService] Error getting user organizations:', error);
            throw error;
        }
    }

    /**
     * Execute an N8N workflow with authentication
     * @param {string} workflowId - N8N workflow webhook ID
     * @param {object} data - Data to send to workflow
     * @param {string} baseUrl - N8N base URL (optional, defaults to env)
     * @returns {Promise<object>} Workflow response
     */
    async executeWorkflow(workflowId, data, baseUrl = null) {
        try {
            if (!this.isAuthenticated()) {
                throw new Error('User must be authenticated to execute workflows');
            }

            const n8nBaseUrl = baseUrl || 'https://flows.smartfront.cl';
            const url = `${n8nBaseUrl}/webhook/${workflowId}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.getAccessToken()}`
                },
                body: JSON.stringify({
                    ...data,
                    user_id: this.user.id,
                    user_email: this.user.email,
                    timestamp: new Date().toISOString()
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[AuthService] Workflow error:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const responseText = await response.text();
            if (!responseText.trim()) {
                throw new Error('Empty response from workflow');
            }

            return JSON.parse(responseText);
        } catch (error) {
            console.error('[AuthService] Workflow execution error:', error);
            throw error;
        }
    }

    /**
     * Upload a file to an N8N workflow
     * @param {string} workflowId - N8N workflow webhook ID
     * @param {File} file - File to upload
     * @param {object} additionalData - Additional data to send
     * @param {string} baseUrl - N8N base URL (optional)
     * @returns {Promise<object>} Workflow response
     */
    async uploadFile(workflowId, file, additionalData = {}, baseUrl = null) {
        try {
            if (!this.isAuthenticated()) {
                throw new Error('User must be authenticated to upload files');
            }

            const n8nBaseUrl = baseUrl || 'https://flows.smartfront.cl';
            const url = `${n8nBaseUrl}/webhook/${workflowId}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('user_id', this.user.id);
            formData.append('user_email', this.user.email);
            formData.append('timestamp', new Date().toISOString());

            // Add additional data
            Object.keys(additionalData).forEach(key => {
                formData.append(key, additionalData[key]);
            });

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.getAccessToken()}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('[AuthService] File upload error:', error);
            throw error;
        }
    }

    /**
     * Query Supabase table with authentication
     * Convenience method for authenticated queries
     * @param {string} table - Table name
     * @returns {object} Supabase query builder
     */
    from(table) {
        if (!this._initialized) {
            throw new Error('AuthService not initialized');
        }
        return this.supabase.from(table);
    }

    /**
     * Get direct access to Supabase client
     * For advanced use cases
     * @returns {object} Supabase client
     */
    getClient() {
        return this.supabase;
    }
}

// Export for ES modules
export default AuthService;

// Also export as window global for non-module usage
if (typeof window !== 'undefined') {
    window.AuthService = AuthService;
}
